/* eslint-disable*/
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useTypedSelector } from 'src/hooks/useTypedSelector';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import YearEndFormsService from 'src/services/year-end-forms.service';
import { PageTitle, ArrowBack } from 'src/modules/notifications/styles';
import { HeaderLeftItems, NavTextLink, HeaderLeftFirstItem, HeaderRightItems } from 'src/modules/employee-list/styles';
import { DefaultTable } from 'src/components';
import { CardContainer, CardHeader, LeftHeader, CardContent } from '../styles';
import Arrow from 'src/assets/arrow-left-black.svg';
import { IW2FormTable } from 'src/interfaces/w2-form-table';
import useDebounce from 'src/hooks/useDebounce';
import { DataTableCellEditButton, SearchInput } from 'pg-ui-library';
import { IHealthCoverageTable } from 'src/interfaces/health-coverage-table';

const editButtonWrapper = (onClick: Function) => <DataTableCellEditButton size={30} onClick={() => onClick()} />;

const customStyles = {
  headRow: {
    style: {
      border: 'none',
    },
  },
  headCells: {
    style: {
      color: '#9399B2',
      font: 'normal normal bold 15px Biotif',
    },
  },
  rows: {
    style: {
      font: 'normal normal normal 14px Biotif',
      color: '#1B1D35',
      borderRadius: '5px',
      '&:not(:last-of-type)': {
        borderBottomWidth: '0px',
      },
    },
    stripedStyle: {
      color: '#1B1D35',
      backgroundColor: '#F3F4F8',
    },
    highlightOnHoverStyle: {
      backgroundColor: 'rgb(230, 244, 244)',
      borderBottomColor: '#FFFFFF',
      borderRadius: '25px',
      outline: '1px solid #FFFFFF',
    },
  },
  cells: {
    style: {
      fontWeight: 'bold',
      justifyContent: 'left',
      paddingTop: '20px',
      paddingBottom: '20px',
    },
  },
  pagination: {
    style: {
      border: 'none',
      svg: {
        fill: 'rgba(0,0,0,0.54)',
      },
    },
  },
};

const FormsTable: React.FC = () => {
  const { t } = useTranslation();
  const { year } = useParams();
  const { formType } = useParams();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const { data: dataCompanies } = useTypedSelector(state => state.config);
  const { data: StatementForm, isLoading } = useQuery(['statement-list-selected'], () =>
    YearEndFormsService.getSelectedyearEndFormsList(
      dataCompanies.config?.selectedCompanyId as unknown as string,
      year ? year : '',
      formType ? formType : ''
    )
  );
  const columnsW2Form = [
    {
      name: <div>Employee Id</div>,
      selector: (row: IW2FormTable) => (row.employeeId ? row.employeeId : ''),
      wrap: true,
    },
    {
      name: <div>Employee Name</div>,
      selector: (row: IW2FormTable) => (row.firstName ? row.firstName + ' ' + row.middleName + ' ' + row.lastName : ''),
      wrap: true,
    },
    {
      name: <div>Control Number</div>,
      selector: (row: IW2FormTable) => (row.controlNumber ? row.controlNumber : ''),
      wrap: true,
    },
    {
      name: <div>Federal Wages</div>,
      selector: (row: IW2FormTable) => (row.federalWages ? '$ ' + row.federalWages : ''),
      wrap: true,
    },
    {
      name: <div>Federal Tax With Held</div>,
      selector: (row: IW2FormTable) => (row.federalTaxWithHeld ? '$ ' + row.federalTaxWithHeld : ''),
      wrap: true,
    },
    {
      name: <div>Social Security Wages</div>,
      selector: (row: IW2FormTable) => (row.socialSecurityWages ? '$ ' + row.socialSecurityWages : ''),
      wrap: true,
    },
    {
      name: <div>Medicare Wages</div>,
      selector: (row: IW2FormTable) => (row.medicareWages ? '$ ' + row.medicareWages : ''),
      wrap: true,
    },
    {
      name: <div>Medicare Tax With Held</div>,
      selector: (row: IW2FormTable) => (row.medicareTaxWithHeld ? '$ ' + row.medicareTaxWithHeld : ''),
      wrap: true,
    },
    {
      name: <div>Social Security Tips</div>,
      selector: (row: IW2FormTable) => (row.socialSecurityTips ? '$ ' + row.socialSecurityTips : ''),
      wrap: true,
    },
    {
      name: <div>Allocated Tips</div>,
      selector: (row: IW2FormTable) => (row.allocatedTips ? '$ ' + row.allocatedTips : ''),
      wrap: true,
    },
    {
      name: <div>Dependent Care Benefits</div>,
      selector: (row: IW2FormTable) => (row.dependentCareBenefits ? '$ ' + row.dependentCareBenefits : ''),
      wrap: true,
    },
    {
      name: <div>Department</div>,
      selector: (row: IW2FormTable) => (row.department ? row.department : ''),
      wrap: true,
    },
    {
      name: 'Link',
      button: true,
      cell: (row: any) => (
        <a
          href={YearEndFormsService.getW2YearEndFormPdf(
            useTypedSelector(state => state.config.data?.config?.selectedCompanyId || ''),
            row.controlNumber,
            true,
            row.employeeId,
            row.year
          )}
          rel="noopener noreferrer"
        >
          Download
        </a>
      ),
    },
    {
      name: <div>Edit</div>,
      cell: (row: any) => editButtonWrapper(() => handleEdit(row)),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];
  const columns1095C = [
    {
      name: <div>Employee Id</div>,
      selector: (row: IHealthCoverageTable) => (row.employeeId ? row.employeeId : ''),
      wrap: true,
    },
    {
      name: <div>Employee Company</div>,
      selector: (row: IHealthCoverageTable) => (row.company ? row.company : ''),
      wrap: true,
    },
    {
      name: <div>Employee Name</div>,
      selector: (row: IHealthCoverageTable) =>
        row.firstName || row.middleName || row.lastName
          ? `${row.firstName ? row.firstName : ''} ${row.middleName ? row.middleName : ''} ${
              row.lastName ? row.lastName : ''
            }`
          : '',
      wrap: true,
    },
    {
      name: <div>Employee Share Year</div>,
      selector: (row: IHealthCoverageTable) => (row.employeeShareYear ? row.employeeShareYear : ''),
      wrap: true,
    },
    {
      name: <div>Year</div>,
      selector: (row: IHealthCoverageTable) => (row.year ? row.year : ''),
      wrap: true,
    },
    {
      name: 'Link',
      button: true,
      cell: (row: any) => (
        <a
          href={YearEndFormsService.get1095YearEndFormPdf(
            useTypedSelector(state => state.config.data?.config?.selectedCompanyId || ''),
            row.controlNumber,
            true,
            row.employeeId,
            row.year
          )}
          target="_blank"
          rel="noopener noreferrer"
        >
          Download
        </a>
      ),
    },
  ];

  const filteredData = useMemo(() => {
    return (
      StatementForm?.data?.filter(
        (row: IW2FormTable) =>
          row.employeeId.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          row.controlNumber.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
          (row.firstName + ' ' + row.middleName + ' ' + row.lastName)
            .toLowerCase()
            .includes(debouncedSearchTerm.toLowerCase())
      ) || []
    );
  }, [StatementForm?.data, debouncedSearchTerm]);

  const navigate = useNavigate();
  const handleEdit = useCallback(
    (row: IW2FormTable) => {
      navigate(`/year-end-forms/${year}/${formType}/${row.employeeId}/${row.controlNumber}`);
    },
    [navigate]
  );

  return (
    <CardContainer>
      <HeaderLeftItems>
        <HeaderLeftFirstItem>
          <NavTextLink to="/year-end-forms">
            <ArrowBack src={Arrow} />
          </NavTextLink>
          <PageTitle>{t('yearEndForms.title')}</PageTitle>
        </HeaderLeftFirstItem>
        <HeaderRightItems>
          <SearchInput
            value={searchTerm}
            placeholder="Search..."
            handleChange={event => {
              setSearchTerm(event.target.value);
            }}
          />
        </HeaderRightItems>
      </HeaderLeftItems>

      <CardHeader>
        <LeftHeader></LeftHeader>
      </CardHeader>
      {formType === 'w2-form' ? (
        <DefaultTable
          columns={columnsW2Form}
          customStyles={customStyles}
          data={filteredData}
          pagination
          paginationServer
          progressPending={isLoading}
          paginationTotalRows={10}
          sortServer
          responsive
        />
      ) : (
        <DefaultTable
          columns={columns1095C}
          customStyles={customStyles}
          data={filteredData}
          pagination
          paginationServer
          progressPending={isLoading}
          paginationTotalRows={10}
          sortServer
          responsive
        />
      )}
      <CardContent></CardContent>
    </CardContainer>
  );
};

export default FormsTable;
